import type { NavItem, NavSection } from "@degaus/docs/nav";
import { NAV_SECTIONS } from "@degaus/docs/nav";

export function docHref(item: NavItem): string {
  return item.slug ? `/docs/${item.slug}` : "/docs";
}

/** The sidebar section and item for a docs path, if the path is in the nav. */
export function findDocLocation(
  pathname: string,
): { section: NavSection; item: NavItem } | null {
  const path = pathname.replace(/\/+$/, "") || "/";
  for (const section of NAV_SECTIONS) {
    const item = section.items.find((entry) => docHref(entry) === path);
    if (item) return { section, item };
  }
  return null;
}

export type DocHeading = { depth: 2 | 3 | 4; text: string; id: string };

function slugify(text: string): string {
  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "section"
  );
}

/**
 * Hands out heading ids in document order, suffixing repeats (-2, -3...).
 * extractHeadings and the renderer each make one per page so their ids match.
 */
export function createHeadingIds(): (text: string) => string {
  const seen = new Map<string, number>();
  return (text) => {
    const base = slugify(text);
    const count = (seen.get(base) ?? 0) + 1;
    seen.set(base, count);
    return count === 1 ? base : `${base}-${count}`;
  };
}

/** h2 to h4 headings of a markdown page (outside fenced code), with their ids. */
export function extractHeadings(markdown: string): DocHeading[] {
  const nextId = createHeadingIds();
  const headings: DocHeading[] = [];
  let inFence = false;
  for (const line of markdown.split("\n")) {
    if (/^\s*(```|~~~)/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const match = /^(#{2,4})\s+(.+?)\s*#*\s*$/.exec(line);
    if (!match?.[1] || !match[2]) continue;
    const text = match[2]
      .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
      .replace(/[`*_]/g, "")
      .trim();
    headings.push({
      depth: match[1].length as DocHeading["depth"],
      text,
      id: nextId(text),
    });
  }
  return headings;
}
